import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartment, DEPARTMENT_OPTIONS } from '../../contexts/DepartmentContext';
import { Button } from '../ui/button';
import { LogOut, Menu, X, ChevronDown, ChevronRight, Building2, User, Settings as SettingsIcon, Star, GripVertical, RefreshCw, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import Logo from '../Logo';
import NotificationBell from '../NotificationBell';
import ViewModeToggle from '../ViewModeToggle';
// iter154 — GlobalSearch import removed; the component file is still on disk
// (/app/frontend/src/components/GlobalSearch.js) so we can re-enable it with a
// single import + JSX line if/when a global search returns to the header.
import AnnouncementsBanner from '../AnnouncementsBanner';
import ImpersonationBanner from '../ImpersonationBanner';
import PreviewBanner from '../PreviewBanner';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

/**
 * Generic sidebar layout used by Admin + Employee shells.
 * Props:
 *   sections: [{ label: 'OPERATIONS', items: [{ path, label, icon, testId, badge? }] }]
 *   portalLabel: 'Admin Console' | 'Field Crew' | ...
 *   showDepartmentSwitcher: bool   // typically only super_admin/admin
 *   testIdPrefix: 'admin' | 'employee'
 */
import ThemeToggle from '../ThemeToggle';
import { resetAndReload } from '../../lib/appRefresh';

const SidebarLayout = ({ sections, hubTabs = [], portalLabel, showDepartmentSwitcher, testIdPrefix = 'nav', topBanner = null }) => {
  const { user, logout } = useAuth();
  const { activeDepartment, setActiveDepartment } = useDepartment();
  // iter318 — top switcher = global TRADE context (canonical active Trades from /api/inv/departments).
  const [divisionOpts, setDivisionOpts] = React.useState(DEPARTMENT_OPTIONS.slice(0, 1));
  React.useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inv/departments`, { withCredentials: true })
      .then((r) => {
        const opts = [DEPARTMENT_OPTIONS[0], ...(r.data || []).filter((t) => t.active !== false).map((t) => ({ value: t.id, label: t.name }))];
        setDivisionOpts(opts);
        if (activeDepartment !== 'all' && !opts.some((o) => o.value === activeDepartment)) setActiveDepartment('all');
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // iter110 — Collapsible desktop sidebar (icons-only at 64px when collapsed).
  // Persisted per user so Dan's preference sticks across sessions. Schedule.js's
  // "Focus" button dispatches a `nexflow:set-sidebar-collapsed` custom event to
  // drive this without prop-drilling through the layout tree.
  const collapseStorageKey = `nexflow.sidebar.collapsed.${user?.id || 'guest'}.${testIdPrefix}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(collapseStorageKey) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(collapseStorageKey, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed, collapseStorageKey]);
  useEffect(() => {
    const onExt = (e) => {
      if (typeof e?.detail?.collapsed === 'boolean') setCollapsed(e.detail.collapsed);
      else setCollapsed((v) => !v);
    };
    window.addEventListener('nexflow:set-sidebar-collapsed', onExt);
    return () => window.removeEventListener('nexflow:set-sidebar-collapsed', onExt);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // ---- Favorites (Pin-to-Top) — persisted per user across devices ----
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const dragIdxRef = useRef(null);

  // Build a lookup of every nav item available to this user, so we can render rich favorites
  const navIndex = useMemo(() => {
    const idx = {};
    sections.forEach((s) => (s.items || []).forEach((it) => { if (it.path) idx[it.path] = { ...it, section: s.label }; }));
    // iter75 — Hub tabs are pinnable to Favorites even though they don't appear as sidebar items.
    // We register them here so the Favorites section can render them with their label + icon.
    hubTabs.forEach((t) => { if (t.path) idx[t.path] = { ...t }; });
    return idx;
  }, [sections, hubTabs]);

  useEffect(() => {
    // iter82 — load once on mount + refresh on the cross-component favorites event. Previously
    // depended on `[navIndex]` which churns identity every parent render (AdminLayout builds
    // `sections` and `hubTabs` inline), causing a re-render storm + constant API polling
    // (visible as the sidebar blinking on production).
    let cancel = false;
    const load = () => {
      axios.get(`${API}/user/favorites`, { withCredentials: true })
        .then((r) => {
          if (cancel) return;
          const saved = r.data?.favorites || [];
          const list = saved.length > 0 ? saved : (r.data?.defaults || []);
          setFavorites(list);  // Filter against navIndex at render-time (see below).
          setFavoritesLoaded(true);
        })
        .catch(() => { if (!cancel) setFavoritesLoaded(true); });
    };
    load();
    const onChange = () => load();
    window.addEventListener('nexflow:favorites-changed', onChange);
    return () => { cancel = true; window.removeEventListener('nexflow:favorites-changed', onChange); };
  }, []);

  // Render-time filter: only show favorites the user can still reach.
  // iter319 — never show a route twice: favorites already in the role's primary section are omitted.
  const primaryPaths = useMemo(
    () => new Set(sections.filter((sec) => sec.primary).flatMap((sec) => sec.items.map((it) => it.path))),
    [sections],
  );
  const visibleFavorites = useMemo(
    () => favorites.filter((p) => !!navIndex[p] && !primaryPaths.has(p)),
    [favorites, navIndex, primaryPaths],
  );

  const persistFavorites = (next) => {
    axios.put(`${API}/user/favorites`, { favorites: next }, { withCredentials: true })
      .then(() => window.dispatchEvent(new Event('nexflow:favorites-changed')))
      .catch(() => { /* silent */ });
  };

  const togglePin = (path) => {
    setFavorites((cur) => {
      const next = cur.includes(path) ? cur.filter((p) => p !== path) : [...cur, path];
      persistFavorites(next);
      return next;
    });
  };

  const onDragStart = (idx) => (e) => { dragIdxRef.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (toIdx) => (e) => {
    e.preventDefault();
    const from = dragIdxRef.current;
    if (from === null || from === toIdx) return;
    setFavorites((cur) => {
      const next = cur.slice();
      const [m] = next.splice(from, 1);
      next.splice(toIdx, 0, m);
      persistFavorites(next);
      return next;
    });
    dragIdxRef.current = null;
  };

  const FavoritesSection = ({ onClickItem }) => {
    if (!favoritesLoaded || visibleFavorites.length === 0) return null;
    return (
      <div data-testid={`${testIdPrefix}-favorites`} className="px-3 mb-2">
        <p className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8A8A] flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" /> Favorites
        </p>
        <ul className="space-y-0.5">
          {favorites.map((path, idx) => {
            const it = navIndex[path];
            if (!it || primaryPaths.has(path)) return null;
            const Icon = it.icon;
            const active = isActive(path);
            return (
              <li
                key={path}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={onDrop(idx)}
                data-testid={`${testIdPrefix}-fav-${path.replace(/\//g, '-')}`}
              >
                <Link
                  to={path}
                  onClick={onClickItem}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-md text-[15px] transition-all duration-150 border-l-[3px] ${
                    active
                      ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37] font-bold'
                      : 'border-transparent text-[#E5E5E5] font-medium hover:text-white hover:bg-[#1a1a1a]'
                  }`}
                >
                  <GripVertical className="w-3 h-3 text-[#525252] shrink-0 cursor-grab" />
                  <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? 'text-[#D4AF37]' : 'text-[#A3A3A3] group-hover:text-white'}`} />
                  <span className="flex-1 truncate">{it.label}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(path); }}
                    className="text-[#D4AF37] hover:text-[#F3E5AB] opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Unpin"
                  >
                    <Star className="w-3 h-3 fill-[#D4AF37]" />
                  </button>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Generic nested-nav support: a section item may declare `children: [...]` to
  // render an expandable sub-group (reusable across any section, not just Workforce).
  const flattenItems = (items = []) =>
    items.flatMap((it) => (it.children && it.children.length ? it.children : [it]));

  // Persisted collapsible-section state per user. QuickBooks-style sidebar.
  const storageKey = `nexflow.sidebar.expanded.${user?.id || 'guest'}.${testIdPrefix}`;
  const activeSectionLabel = useMemo(() => {
    for (const sec of sections) {
      if (flattenItems(sec.items || []).some((it) => !it.disabled && isActive(it.path))) return sec.label;
    }
    return null;
  }, [sections, location.pathname]); // eslint-disable-line
  // Composite key of the active sub-group (e.g. "Workforce::Admin Workforce"), if any.
  const activeSubKey = useMemo(() => {
    for (const sec of sections) {
      for (const item of (sec.items || [])) {
        if (item.children && item.children.some((c) => !c.disabled && isActive(c.path))) {
          return `${sec.label}::${item.label}`;
        }
      }
    }
    return null;
  }, [sections, location.pathname]); // eslint-disable-line
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    // First-load default: expand the section containing the active route + 1st section
    const initial = {};
    sections.forEach((s, i) => { initial[s.label] = (i === 0 || s.label === activeSectionLabel); });
    if (activeSubKey) initial[activeSubKey] = true;
    return initial;
  });
  // Auto-expand the active section (and active sub-group) as the user navigates
  useEffect(() => {
    setExpanded((cur) => {
      const next = { ...cur };
      let changed = false;
      if (activeSectionLabel && !cur[activeSectionLabel]) { next[activeSectionLabel] = true; changed = true; }
      if (activeSubKey && !cur[activeSubKey]) { next[activeSubKey] = true; changed = true; }
      return changed ? next : cur;
    });
  }, [activeSectionLabel, activeSubKey]); // eslint-disable-line
  // Persist on change
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(expanded)); } catch { /* ignore */ }
  }, [expanded, storageKey]);

  const toggleSection = (label) => setExpanded((cur) => ({ ...cur, [label]: !cur[label] }));

  // iter71 — Preserve sidebar scroll position across route changes.
  // iter86 — `NavList` is defined inline (so it remounts on each parent render). Using a plain
  // `useRef` here means the ref points to a stale DOM node and the restore silently no-ops.
  // Fix: use a *callback ref* so every fresh <nav> mount re-attaches AND restores scrollTop
  // synchronously before paint. Result: navigating between pages keeps the user pinned to
  // their sidebar position (no flash, no jump).
  // Key includes user + portal so each role/portal has its own remembered position.
  const scrollStorageKey = `nexflow.sidebar.scroll.${user?.id || 'guest'}.${testIdPrefix}`;
  const navRef = useRef(null);
  const setNavRef = (el) => {
    navRef.current = el;
    if (!el) return;
    try {
      const stored = sessionStorage.getItem(scrollStorageKey);
      if (stored != null) {
        const top = parseInt(stored, 10);
        if (!Number.isNaN(top) && top > 0) el.scrollTop = top;
      }
    } catch { /* ignore */ }
  };
  // Throttled writer — every scroll persists the latest scrollTop.
  const writeScrollPos = useRef(null);
  const onSidebarScroll = (e) => {
    const top = e.currentTarget.scrollTop;
    if (writeScrollPos.current) cancelAnimationFrame(writeScrollPos.current);
    writeScrollPos.current = requestAnimationFrame(() => {
      try { sessionStorage.setItem(scrollStorageKey, String(top)); } catch { /* ignore */ }
    });
  };

  const renderLeaf = ({ path, label, icon: Icon, testId, badge, disabled }, onClickItem, indent = false) => {
    const active = isActive(path);
    if (disabled) {
      return (
        <li key={path || label}>
          <div
            data-testid={testId || `${testIdPrefix}-nav-${label.toLowerCase().replace(/\s/g, '-')}-disabled`}
            className={`group flex items-center gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'} py-2 rounded-md text-sm border-l-2 border-transparent text-[#525252] cursor-not-allowed select-none`}
            title="Coming soon"
          >
            <Icon className="w-4 h-4 shrink-0 text-[#3f3f3f]" />
            <span className="flex-1 truncate">{label}</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#262626] text-[#525252] uppercase tracking-wider">soon</span>
          </div>
        </li>
      );
    }
    return (
      <li key={path}>
        <Link
          to={path}
          data-testid={testId || `${testIdPrefix}-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
          onClick={onClickItem}
          className={`group flex items-center gap-3 ${indent ? 'pl-9 pr-3' : 'px-3'} py-2.5 rounded-md text-[15px] transition-all duration-150 border-l-2 ${
            active
              ? 'border-[#D4AF37] bg-[#D4AF37]/20 text-[#F3E5AB] font-bold shadow-[inset_0_0_0_1px_rgba(212,175,55,0.18)]'
              : 'border-transparent text-[#D4D4D4] font-medium hover:text-white hover:bg-[#1a1a1a]'
          }`}
        >
          <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? 'text-[#D4AF37]' : 'text-[#A3A3A3] group-hover:text-white'}`} />
          <span className="flex-1 truncate">{label}</span>
          {badge && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">{badge}</span>
          )}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(path); }}
            data-testid={`${testIdPrefix}-pin-${label.toLowerCase().replace(/\s/g, '-')}`}
            className={`shrink-0 ${favorites.includes(path) ? 'text-[#D4AF37] opacity-100' : 'text-[#525252] opacity-0 group-hover:opacity-100'} hover:text-[#F3E5AB] transition-opacity`}
            title={favorites.includes(path) ? 'Unpin from favorites' : 'Pin to favorites'}
          >
            <Star className={`w-3 h-3 ${favorites.includes(path) ? 'fill-[#D4AF37]' : ''}`} />
          </button>
        </Link>
      </li>
    );
  };

  const NavList = ({ onClickItem }) => (
    <nav
      ref={setNavRef}
      onScroll={onSidebarScroll}
      data-testid={`${testIdPrefix}-sidebar-nav`}
      className="flex-1 overflow-y-auto py-3 px-3 space-y-1"
    >
      <FavoritesSection onClickItem={onClickItem} />
      {sections.map((sec) => {
        const isOpen = !!expanded[sec.label];
        const slug = sec.label.toLowerCase().replace(/\s+/g, '-');
        return (
          <div key={sec.label}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-nav-section-${slug}`}
              onClick={() => toggleSection(sec.label)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A8A8A] hover:text-[#D4D4D4] transition-colors"
            >
              <span className="truncate">{sec.label}</span>
              {isOpen ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
            </button>
            {isOpen && (
              <ul className="space-y-0.5 mb-1 mt-0.5">
                {sec.items.map((item) => {
                  if (item.children && item.children.length) {
                    const subKey = `${sec.label}::${item.label}`;
                    const subOpen = !!expanded[subKey];
                    const subSlug = item.label.toLowerCase().replace(/\s+/g, '-');
                    const SubIcon = item.icon;
                    const subActive = item.children.some((c) => isActive(c.path));
                    return (
                      <li key={subKey}>
                        <button
                          type="button"
                          data-testid={`${testIdPrefix}-nav-subsection-${subSlug}`}
                          onClick={() => toggleSection(subKey)}
                          aria-expanded={subOpen}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[15px] border-l-2 transition-colors ${
                            subActive ? 'border-[#D4AF37]/60 text-[#F3E5AB] font-semibold' : 'border-transparent text-[#D4D4D4] font-medium hover:text-white hover:bg-[#1a1a1a]'
                          }`}
                        >
                          {SubIcon && <SubIcon className={`w-[18px] h-[18px] shrink-0 ${subActive ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`} />}
                          <span className="flex-1 truncate text-left">{item.label}</span>
                          {subOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                        {subOpen && (
                          <ul className="space-y-0.5 mt-0.5 ml-4 border-l border-[#1f1f1f]">
                            {item.children.map((child) => renderLeaf(child, onClickItem, true))}
                          </ul>
                        )}
                      </li>
                    );
                  }
                  return renderLeaf(item, onClickItem, false);
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );


  const SidebarInner = ({ onClickItem }) => (
    <div className="flex flex-col h-full bg-[#0D0D0D] border-r border-[#1f1f1f]">
      <div className="px-5 py-5 border-b border-[#1f1f1f] flex items-center justify-between">
        <Logo size="sm" showTagline={false} tone="dark-bg" />
        <button
          data-testid="sidebar-close-mobile"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-[#A3A3A3] hover:text-white"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="px-5 pt-3 pb-3 text-[11px] uppercase tracking-[0.22em] text-white font-bold">{portalLabel}</p>
      <NavList onClickItem={onClickItem} />
      <div className="border-t border-[#1f1f1f] p-4 text-[11px] text-[#525252]">
        © Nexflow Inc. <span className="text-[#A3A3A3]">— v15</span>
      </div>
    </div>
  );

  // iter111 — Collapsed sidebar: ONE icon per section (hub), not every item.
  // Hovering / focusing a hub icon opens a flyout panel to the right with that
  // section's items. Clicking the hub icon navigates to that section's first item
  // (its landing page). This matches the user's "around 6 main hubs" request and
  // replaces the wall-of-30-icons we shipped in iter110.
  const [flyoutSection, setFlyoutSection] = useState(null);  // section.label or null
  const CollapsedSidebar = () => {
    return (
      <div className="flex flex-col h-full bg-[#0D0D0D] border-r border-[#1f1f1f] w-16 items-center relative">
        <button
          onClick={() => setCollapsed(false)}
          className="w-12 h-12 mt-3 mb-2 rounded-md flex items-center justify-center text-[#D4AF37] hover:bg-[#1a1a1a]"
          title="Expand sidebar"
          data-testid={`${testIdPrefix}-sidebar-expand-logo`}
        >
          <img src="/nexflow-logo.png" alt="Nexflow" className="h-8 w-8 object-contain" />
        </button>
        <div className="border-t border-[#1f1f1f] w-10 my-2" />
        <nav
          data-testid={`${testIdPrefix}-sidebar-nav-collapsed`}
          className="flex-1 overflow-y-auto w-full flex flex-col items-center gap-2 py-2"
          onMouseLeave={() => setFlyoutSection(null)}
        >
          {sections.map((sec) => {
            const flat = flattenItems(sec.items);
            const HubIcon = sec.hubIcon || (sec.items[0]?.icon) || (flat[0]?.icon);
            if (!HubIcon) return null;
            const sectionActive = flat.some((it) => isActive(it.path));
            // Sum any pending badges on items in this section so dispatch sees
            // counts (Service Requests, Job Approvals, etc.) at a glance.
            const sectionBadge = flat.reduce((acc, it) => {
              const n = parseInt(it.badge || '0', 10) || 0;
              return acc + n;
            }, 0);
            return (
              <button
                key={sec.label}
                data-testid={`${testIdPrefix}-hub-${sec.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                onMouseEnter={() => setFlyoutSection(sec.label)}
                onFocus={() => setFlyoutSection(sec.label)}
                onClick={() => {
                  // Click navigates to the section's first item (hub landing) and keeps flyout open.
                  const first = flat[0];
                  if (first?.path) {
                    navigate(first.path);
                    setFlyoutSection(sec.label);
                  }
                }}
                title={sec.label}
                className={`relative w-11 h-11 rounded-lg flex items-center justify-center transition-all ${
                  sectionActive
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37] shadow-[inset_0_0_0_1px_rgba(212,175,55,0.4)]'
                    : 'text-[#A3A3A3] hover:bg-[#1a1a1a] hover:text-white'
                }`}
              >
                <HubIcon className="w-5 h-5" />
                {sectionBadge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {sectionBadge > 99 ? '99+' : sectionBadge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        {/* Flyout — listed items for the hovered hub, anchored to the right of the rail. */}
        {flyoutSection && (() => {
          const sec = sections.find((s) => s.label === flyoutSection);
          if (!sec) return null;
          return (
            <div
              data-testid={`${testIdPrefix}-hub-flyout`}
              onMouseEnter={() => setFlyoutSection(sec.label)}
              onMouseLeave={() => setFlyoutSection(null)}
              className="absolute left-16 top-16 w-60 bg-[#0F0F0F] border border-[#262626] rounded-md shadow-xl z-50 py-2"
            >
              <p className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold border-b border-[#1f1f1f] mb-1">
                {sec.label}
              </p>
              <div className="max-h-[60vh] overflow-y-auto py-1">
                {sec.items.map((item) => {
                  if (item.children && item.children.length) {
                    return (
                      <div key={`grp-${item.label}`} className="mt-1">
                        <p className="px-4 pt-2 pb-1 text-[9px] uppercase tracking-[0.18em] text-[#8A8A8A] font-bold">{item.label}</p>
                        {item.children.filter((c) => !c.disabled).map(({ path, label, icon: Icon, testId, badge }) => {
                          const active = isActive(path);
                          return (
                            <Link
                              key={path}
                              to={path}
                              onClick={() => setFlyoutSection(null)}
                              data-testid={testId || `${testIdPrefix}-flyout-${label.toLowerCase().replace(/\s/g, '-')}`}
                              className={`flex items-center gap-3 pl-6 pr-4 py-2 text-sm transition-colors ${active ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-[#D4D4D4] hover:bg-[#1a1a1a] hover:text-white'}`}
                            >
                              <Icon className="w-4 h-4 shrink-0" />
                              <span className="truncate flex-1">{label}</span>
                              {badge && (
                                <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{badge}</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  }
                  if (item.disabled) return null;
                  const { path, label, icon: Icon, testId, badge } = item;
                  const active = isActive(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setFlyoutSection(null)}
                      data-testid={testId || `${testIdPrefix}-flyout-${label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                        active
                          ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
                          : 'text-[#D4D4D4] hover:bg-[#1a1a1a] hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate flex-1">{label}</span>
                      {badge && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                          {badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex pl-safe pr-safe">
      {/* Desktop sidebar — width swaps based on collapsed state. iter110. */}
      <aside
        data-testid={`${testIdPrefix}-sidebar`}
        data-collapsed={collapsed ? '1' : '0'}
        className={`hidden lg:flex shrink-0 sticky top-0 h-screen relative ${collapsed ? 'w-16' : 'w-60'}`}
      >
        {collapsed ? <CollapsedSidebar /> : <SidebarInner />}
        {/* Edge chevron — always visible on desktop, pinned to the right edge of the aside. */}
        <button
          data-testid={`${testIdPrefix}-sidebar-edge-toggle`}
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden lg:flex absolute -right-3 top-20 z-20 w-6 h-6 rounded-full bg-[#141414] border border-[#262626] hover:border-[#D4AF37] text-[#A3A3A3] hover:text-[#D4AF37] items-center justify-center shadow-md transition-colors"
        >
          {collapsed ? <PanelLeftOpen className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-3.5 h-3.5" />}
        </button>
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <>
          <div
            data-testid="sidebar-backdrop"
            className="lg:hidden fixed inset-0 bg-black/70 z-40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="lg:hidden fixed left-0 top-0 h-screen w-64 z-50">
            <SidebarInner onClickItem={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="bg-[#0D0D0D] border-b border-[#1f1f1f] sticky top-0 z-30 pt-safe-header">
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 lg:px-6 h-14 sm:h-16">
            <button
              data-testid="sidebar-open-mobile"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden shrink-0 text-[#A3A3A3] hover:text-white"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            {/* iter110 — Desktop top-bar sidebar toggle (mirrors the edge chevron). */}
            <button
              data-testid={`${testIdPrefix}-sidebar-topbar-toggle`}
              onClick={() => setCollapsed((v) => !v)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden lg:flex shrink-0 w-9 h-9 rounded-md items-center justify-center text-[#A3A3A3] hover:text-[#D4AF37] hover:bg-[#1a1a1a] transition-colors"
            >
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            {/* iter154 — Global header search hidden per user request. The All Invoices
                page (and other list pages) keep their own page-scoped filters/search. We
                preserve the import + component file so this is a one-line revert when /
                if global search is wanted again. The flex-1 spacer below keeps the header
                layout (toggles, notifications, user menu) right-aligned without the gap. */}
            <div className="flex-1 min-w-0" aria-hidden="true" />
            <div className="flex items-center gap-2 lg:gap-3">
              {showDepartmentSwitcher && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="dept-switcher-trigger"
                      className="hidden md:flex items-center gap-2 px-3 py-2 rounded-md border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#D4AF37] text-xs transition-colors"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      <span className="uppercase tracking-wider">
                        {divisionOpts.find((d) => d.value === activeDepartment)?.label || 'All Trades'}
                      </span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-white">
                    <DropdownMenuLabel className="text-[#525252] text-[10px] uppercase tracking-wider">Trade context</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[#262626]" />
                    {divisionOpts.map((d) => (
                      <DropdownMenuItem
                        key={d.value}
                        data-testid={`dept-option-${d.value}`}
                        onClick={() => setActiveDepartment(d.value)}
                        className={`cursor-pointer text-sm ${activeDepartment === d.value ? 'text-[#D4AF37]' : 'text-white'}`}
                      >
                        {d.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <ViewModeToggle userRole={user?.role} />
              <NotificationBell />
              <ThemeToggle />
              {/* Profile dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="profile-menu-trigger"
                    className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-[#1a1a1a] transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                      {(user?.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="hidden lg:inline text-sm text-white max-w-[140px] truncate">{user?.name}</span>
                    <ChevronDown className="hidden lg:inline w-3 h-3 text-[#A3A3A3]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-white w-52">
                  <DropdownMenuLabel className="text-[#A3A3A3]">
                    <p className="text-white truncate">{user?.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">{user?.role} · {user?.department || '—'}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="profile-menu-item"
                    onClick={() => {
                      const role = user?.role;
                      const adminRoles = ['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'];
                      if (adminRoles.includes(role)) navigate('/admin/profile');
                      else if (role === 'employee' || role === 'technician') navigate('/employee/profile');
                      else if (role === 'vip') navigate('/vip/profile');
                      else navigate('/customer/profile');
                    }}
                    className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                  >
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  {(user?.role === 'super_admin' || user?.role === 'admin') && (
                    <DropdownMenuItem
                      data-testid="settings-menu-item"
                      onClick={() => navigate('/admin/settings')}
                      className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                    >
                      <SettingsIcon className="w-4 h-4 mr-2" /> System Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  {/* iter87 — Safe manual reset for stuck PWA / stale cache. Preserves drafts,
                      pinned favorites, sidebar scroll, and the auth cookie. */}
                  <DropdownMenuItem
                    data-testid="refresh-app-menu-item"
                    onClick={async () => {
                      const ok = window.confirm(
                        'Refresh App — clear stale cache and reload?\n\n' +
                        'This is safe to use:\n' +
                        '• You will stay logged in.\n' +
                        '• Job completion drafts, invoice drafts, and typed notes are preserved.\n' +
                        '• Pinned favorites and sidebar scroll position are preserved.'
                      );
                      if (!ok) return;
                      await resetAndReload();
                    }}
                    className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh App
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="logout-menu-item"
                    onClick={handleLogout}
                    className="cursor-pointer text-sm text-white hover:!bg-[#7F1D1D]/30 hover:!text-red-300 focus:!bg-[#7F1D1D]/30 focus:!text-red-300"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                data-testid={`${testIdPrefix}-logout-button`}
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="hidden xl:inline-flex text-[#A3A3A3] hover:text-white hover:bg-[#1a1a1a]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Content */}
        <ImpersonationBanner />
        <PreviewBanner />
        {topBanner}
        <main className="flex-1 px-4 sm:px-5 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-[1400px] w-full mx-auto pb-safe">
          <AnnouncementsBanner />
          {/* iter88 — Per-route boundary; keyed on pathname so navigating to another page
              clears any error from the previous one. Sidebar stays usable on crashes. */}
          <ErrorBoundary scope={`${testIdPrefix || 'page'}:${location.pathname}`} key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default SidebarLayout;
